import Iconify from 'src/components/iconify';
import { LabelColor } from 'src/components/label';
import type { NavSectionProps } from 'src/components/nav-section';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { Roles } from 'src/types/User';

// ----------------------------------------------------------------------

const ICONS = {
  visit: <Iconify icon="mdi:hospital-box" />,
  waitingRoom: <Iconify icon="mdi:human-queue" />,
  visitsHistory: <Iconify icon="mdi:clipboard-text-history-outline" />,
  statistics: <Iconify icon="mdi:chart-box-outline" />,
  calendar: <Iconify icon="mdi:calendar-month" />,
  templates: <Iconify icon="mdi:folder-star-outline" />,
  forms: <Iconify icon="mdi:form-select" />,
  professionals: <Iconify icon="mdi:person-group" />,
  patients: <Iconify icon="mdi:paw" />,
  treatments: <Iconify icon="mdi:needle" />,
  voiceReception: <Iconify icon="mdi:phone-in-talk-outline" />,
  warehouse: <Iconify icon="mdi:warehouse" />,
  warehouseProducts: <Iconify icon="mdi:package-variant-closed" />,
  warehouseDemand: <Iconify icon="mdi:clipboard-list-outline" />,
  warehouseSales: <Iconify icon="mdi:cart-outline" />,
  warehouseDocuments: <Iconify icon="mdi:file-document-multiple-outline" />,
  warehouseSuppliers: <Iconify icon="mdi:truck-outline" />,
};

interface DashboardNavigationAccess {
  readonly isOrganizationAdmin?: boolean;
  readonly isOrganizationOwner?: boolean;
  readonly isProfessional?: boolean;
  readonly selectedRole: Roles | null;
}

interface DashboardNavigationContext {
  readonly isLive?: boolean;
  readonly showWarehouse?: boolean;
  readonly warehouseEnabled?: boolean;
  readonly access: DashboardNavigationAccess;
}

const warehouseNavItems = (
  showWarehouse: boolean,
  warehouseEnabled: boolean,
  canManageWarehouse: boolean,
): NavSectionProps['data'][number]['items'] =>
  showWarehouse
    ? [
        {
          title: 'nav.warehouse',
          path: PATH_DASHBOARD.warehouse.root,
          icon: ICONS.warehouse,
          children: [
            {
              title: 'warehouse.common.tabs.products',
              path: PATH_DASHBOARD.warehouse.products,
              icon: ICONS.warehouseProducts,
            },
            ...(warehouseEnabled
              ? [
                  {
                    title: 'warehouse.common.tabs.demand',
                    path: PATH_DASHBOARD.warehouse.demand,
                    icon: ICONS.warehouseDemand,
                  },
                ]
              : []),
            {
              title: 'warehouse.common.tabs.sales',
              path: PATH_DASHBOARD.warehouse.sales,
              icon: ICONS.warehouseSales,
            },
            {
              title: 'warehouse.common.tabs.documents',
              path: PATH_DASHBOARD.warehouse.documents,
              icon: ICONS.warehouseDocuments,
            },
            ...(canManageWarehouse
              ? [
                  {
                    title: 'warehouse.common.tabs.suppliers',
                    path: PATH_DASHBOARD.warehouse.suppliers,
                    icon: ICONS.warehouseSuppliers,
                  },
                ]
              : []),
          ],
        },
      ]
    : [];

const getNavConfig = ({
  isLive,
  showWarehouse = false,
  warehouseEnabled = false,
  access: {
    isOrganizationAdmin = false,
    isOrganizationOwner = false,
    isProfessional = false,
    selectedRole,
  },
}: DashboardNavigationContext): NavSectionProps['data'] => [
  // GENERAL
  // ----------------------------------------------------------------------
  {
    subheader: '',
    items: [
      ...(isProfessional && selectedRole === Roles.professional
        ? [
            {
              title: 'nav.waitingRoom',
              path: PATH_DASHBOARD.general.waitingRoom,
              icon: ICONS.waitingRoom,
              ...(isLive && {
                label: {
                  color: 'error' as LabelColor,
                  text: 'nav.waitingRoomInProgress',
                  animated: true,
                  icon: 'mdi:timer-sand',
                },
              }),
            },
            {
              title: 'nav.calendar',
              path: PATH_DASHBOARD.general.calendar,
              icon: ICONS.calendar,
            },
            {
              title: 'nav.patients',
              path: PATH_DASHBOARD.general.patients.root,
              icon: ICONS.patients,
            },
            {
              title: 'nav.treatments',
              path: PATH_DASHBOARD.general.treatmentsOverview,
              icon: ICONS.treatments,
            },
            ...warehouseNavItems(showWarehouse, warehouseEnabled, false),
            {
              title: 'nav.visitHistory',
              path: PATH_DASHBOARD.general.visitsHistory,
              icon: ICONS.visitsHistory,
            },
            {
              title: 'nav.myStatistics',
              path: PATH_DASHBOARD.general.statistics,
              icon: ICONS.statistics,
            },
            {
              title: 'nav.templates',
              path: PATH_DASHBOARD.general.templates,
              icon: ICONS.templates,
            },
            // {
            //   title: 'Formularze',
            //   path: PATH_DASHBOARD.general.forms,
            //   icon: ICONS.forms,
            // },
          ]
        : []),
      ...((isOrganizationAdmin || isOrganizationOwner) &&
      (selectedRole === Roles.organization_admin ||
        selectedRole === Roles.organization_owner)
        ? [
            {
              title: 'nav.calendar',
              path: PATH_DASHBOARD.general.calendar,
              icon: ICONS.calendar,
            },
            {
              title: 'nav.waitingRoom',
              path: PATH_DASHBOARD.general.waitingRoom,
              icon: ICONS.waitingRoom,
            },
            {
              title: 'nav.patients',
              path: PATH_DASHBOARD.general.patients.root,
              icon: ICONS.patients,
            },
            {
              title: 'nav.specialists',
              path: PATH_DASHBOARD.general.professionals.root,
              icon: ICONS.professionals,
            },
            {
              title: 'nav.visitHistory',
              path: PATH_DASHBOARD.general.visitsHistory,
              icon: ICONS.visitsHistory,
            },
            {
              title: 'nav.treatments',
              path: PATH_DASHBOARD.general.treatmentsOverview,
              icon: ICONS.treatments,
            },
            ...warehouseNavItems(showWarehouse, warehouseEnabled, true),
            {
              title: 'nav.statistics',
              path: PATH_DASHBOARD.general.statistics,
              icon: ICONS.statistics,
            },
            {
              title: 'nav.voiceReception',
              path: PATH_DASHBOARD.general.voiceReceptionist.tab('settings'),
              icon: ICONS.voiceReception,
            },
            // {
            //   title: 'Formularze',
            //   path: PATH_DASHBOARD.general.forms,
            //   icon: ICONS.forms,
            // },
          ]
        : []),
    ],
  },
];

export default getNavConfig;
